import { useState, useEffect } from 'react'
import { Button, Checkbox } from 'antd'
import $t from '../../../i18n'
import Card from '../../common/Card'
import Page from '../../common/Page'
import RelationTag from '../../common/RelationTag'
import SelectedPlanInfo from '../../common/SelectedPlanInfo/SelectedPlanInfo'
import { useDispatch, useSelector } from 'react-redux'
import { dateFormat } from '../../../consts'
import { ssnDisplayValue } from 'utils/ssnUtils'
import Actions from '../../../actions'
import routes from '../../../consts/routes'
import Dialog from '../../common/Dialog'
import dayjs from 'dayjs'
import { STEPS } from 'consts/flowConsts'
import {
  getMemberAddress,
  getOwnerMember,
  sortByRelation
} from 'utils/memberUtils'
import MaterialInput from 'components/common/MaterialInput'
import useApplicationValidation from 'hooks/useApplicationValidation'
import { isEmpty } from 'lodash'

import './Finalize.css'
import { eventTypes } from 'consts/eventConsts'
import { event } from 'consts/eventConsts'
import { convertDate } from '../../../utils/generalUtils'

const getValidationErrors = ({
  signature,
  isAgreedToTerms,
  isUnderstoodTerms,
  ownerMember
}) => {
  const result = {}

  if (!isAgreedToTerms || !isUnderstoodTerms) {
    result.approveTerms = $t('errors.validation.approveTerms')
  }

  if (
    signature?.toLowerCase() !==
      ownerMember?.firstName?.toLowerCase() +
        ' ' +
        ownerMember?.lastName?.toLowerCase() &&
    signature?.toLowerCase() !==
      ownerMember?.lastName?.toLowerCase() +
        ' ' +
        ownerMember?.firstName?.toLowerCase()
  ) {
    result.signature = $t('errors.validation.fullNameNoMatch')
  }

  return (!isEmpty(result) && result) || null
}

const Finalize = () => {
  const dispatch = useDispatch()
  useApplicationValidation()
  const members = useSelector(state => state.member?.members)
  const memberQualifyLifeEvent = useSelector(
    state => state.member?.memberQualifyLifeEvent
  )
  const selectedPlan = useSelector(state => state.plan?.selectedPlan)
  const isMonthlyPremiumAlertOpen = useSelector(
    state => state.plan?.isPlanPriceDialogVisable
  )
  const planPrice = useSelector(
    state => state.plan?.selectedPlan?.plan_rate_multiplied
  )
  const isSaveApplicationLoading = useSelector(
    state => state.application?.isSaveApplicationLoading
  )

  const [isAgreedToTerms, setIsAgreedToTerms] = useState(false)
  const [isUnderstoodTerms, setIsUnderstoodTerms] = useState(false)
  const [consentSignature, setConsentSignature] = useState(null)
  const [shouldValidate, setShouldValidate] = useState(false)
  const [sortMembers, setSortMembers] = useState(members)

  const ownerMember = getOwnerMember(sortMembers)

  useEffect(() => {
    const cloneFamilyMembers = [...members]

    // sorting the array so the spouse will be first
    cloneFamilyMembers?.sort(sortByRelation)
    setSortMembers(cloneFamilyMembers)
  }, [members])

  const errors =
    shouldValidate &&
    getValidationErrors({
      signature: consentSignature,
      isAgreedToTerms,
      isUnderstoodTerms,
      ownerMember
    })

  const onEdit = id => {
    // navigate to specific member details using anchor
    // index is for family members, if not index then it's owner
    dispatch(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        routes.ENROLL_MEMBER_DETAILS + '/#' + id
      )
    )
  }

  const renderMemberCard = member => {
    const { homeAddress, mailingAddress } = getMemberAddress(
      member,
      ownerMember
    )

    return (
      <Card className='member-review-card'>
        <RelationTag relation={member?.relation} />
        <div className='member-details details-container'>
          <div className='field-header semi-bold'>
            {$t('enroll.details.title')}
          </div>
          <div className='row wrap'>
            <div className='field col'>
              <div className='field-title'>{$t('fields.name')}</div>
              <div className='field-value'>
                {member?.firstName + ' ' + member?.lastName}
              </div>
            </div>
            <div className='field col'>
              <div className='field-title'>{$t('fields.dob')}</div>
              <div className='field-value'>
                {member?.dob ? dayjs(member?.dob)?.format(dateFormat) : 'N/A'}
              </div>
            </div>
            <div className='field col'>
              <div className='field-title'>{$t('fields.ssn')}</div>
              <div className='field-value'>{ssnDisplayValue(member)}</div>
            </div>
          </div>
          <div className='row wrap'>
            <div className='field col'>
              <div className='field-title'>{$t('fields.sex')}</div>
              <div className='field-value'>
                {member?.sex ? $t('common.' + member?.sex) : 'N/A'}
              </div>
            </div>
            <div className='field col'>
              <div className='field-title'>{$t('fields.relationship')}</div>
              <div className='field-value'>
                {$t('common.' + member?.relation)}
              </div>
            </div>
            <div className='field col'>
              <div className='field-title'>{$t('fields.tobaco')}</div>
              <div className='field-value'>
                {member?.isTobbacoUser ? $t('common.yes') : $t('common.no')}
              </div>
            </div>
          </div>
        </div>
        <div className='contact-details details-container'>
          <div className='field-header semi-bold'>
            {$t('enroll.details.contactInfo')}
          </div>
          <div className='row wrap'>
            <div className='field col'>
              <div className='field-title'>
                {$t('enroll.details.homeAddress')}
              </div>
              <div className='field-value'>
                {[
                  homeAddress?.address1,
                  homeAddress?.address2,
                  homeAddress?.city,
                  homeAddress?.state + ' ' + homeAddress?.zipCode
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            </div>
          </div>
          <div className='row wrap'>
            <div className='field col'>
              <div className='field-title'>
                {$t('enroll.details.mailingAddress')}
              </div>
              <div className='field-value'>
                {[
                  mailingAddress?.address1,
                  mailingAddress?.address2,
                  mailingAddress?.city,
                  mailingAddress?.state + ' ' + mailingAddress?.zipCode
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            </div>
          </div>
          <div className='row wrap'>
            <div className='field col'>
              <div className='field-title'>{$t('fields.language')}</div>
              <div className='field-value'>
                {member?.prefferedLanguage
                  ? $t('common.languages.' + member?.prefferedLanguage)
                  : 'N/A'}
              </div>
            </div>
            <div className='field col'>
              <div className='field-title'>{$t('fields.phoneNumber')}</div>
              <div className='field-value'>{member?.phone}</div>
            </div>
            <div className='field col'>
              <div className='field-title'>{$t('fields.email')}</div>
              <div className='field-value'>{member?.email}</div>
            </div>
          </div>
        </div>
        <Button className='edit-btn' onClick={() => onEdit(member?.id)}>
          {$t('common.edit')}{' '}
        </Button>
      </Card>
    )
  }

  const renderConsentCard = () => {
    return (
      <Card className='member-consent-card'>
        <div className='field-title'>
          {$t('enroll.finalize.consent.title', {
            name: ownerMember?.firstName + ' ' + ownerMember?.lastName
          })}
        </div>
        <div className='error conset-error'>{errors?.approveTerms}</div>
        <Checkbox
          className='consent-checkbox field-value'
          checked={isAgreedToTerms}
          onChange={e => setIsAgreedToTerms(e?.target?.checked)}
        >
          {$t('enroll.finalize.consent.agree')}
        </Checkbox>
        <Checkbox
          className='consent-checkbox field-value'
          checked={isUnderstoodTerms}
          onChange={e => setIsUnderstoodTerms(e?.target?.checked)}
        >
          {$t('enroll.finalize.consent.understand')}
        </Checkbox>
        <MaterialInput
          label={$t('enroll.finalize.consent.typeFullName')}
          errorText={errors?.signature}
          value={consentSignature || ''}
          onChange={e => setConsentSignature(e?.target?.value)}
        />
      </Card>
    )
  }

  const onConfirm = () => {
    const errors = getValidationErrors({
      signature: consentSignature,
      isAgreedToTerms,
      isUnderstoodTerms,
      ownerMember
    })
    if (errors) {
      setShouldValidate(true)
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: { consentSignature, isAgreedToTerms, isUnderstoodTerms },
          error: errors?.signature,
          element: 'Submit application'
        })
      )
      return
    }
    setShouldValidate(false)
    dispatch(
      Actions.createAction(Actions.STEP_COMPLETED, {
        step: STEPS.FINALIZE,
        isConsentSigned: true
      })
    )
  }


  return (
    <Page
      className='enroll-member-finalize'
      title={$t('enroll.finalize.title')}
    >
      <SelectedPlanInfo
        planName={selectedPlan?.translated_name}
        monthlyPremium={'$' + selectedPlan?.plan_rate_multiplied}
        effectiveDate={convertDate(memberQualifyLifeEvent?.coverageStartDate)}
      />
      {sortMembers?.map(member => renderMemberCard(member))}
      {renderConsentCard()}
      <Button
        type='primary'
        onClick={onConfirm}
        loading={isSaveApplicationLoading}
        className='confirm-btn'
      >
        {$t('enroll.finalize.confirm')}
      </Button>
      <Dialog
        title={$t('dialogs.monthlyPremiumAlert.title')}
        isVisible={isMonthlyPremiumAlertOpen}
        footerActions={[
          <Button
            className='dialog-action'
            type='primary'
            onClick={() =>
              dispatch(Actions.createAction(Actions.PLAN_PRICE_DIALOG_CLOSED))
            }
          >
            {$t('common.continue')}
          </Button>
        ]}
        onCancel={() =>
          dispatch(Actions.createAction(Actions.PLAN_PRICE_DIALOG_CLOSED))
        }
        className='monthly-premium-changed-dialog'
      >
        <div className='dialog-content'>
          <div className='dialog-desc'>
            {$t('dialogs.monthlyPremiumAlert.description')}
          </div>
          <div className='semi-bold dialog-note'>
            {$t('dialogs.monthlyPremiumAlert.note', { price: planPrice })}
          </div>
        </div>
      </Dialog>
    </Page>
  )
}

export default Finalize
